import { formatDate } from '@angular/common';
import { Booking, Item } from '@app/services/graphql.service';
import { FranchiseLocation } from '@app/models/franchise.model';
import { ParsedPet } from '@app/models/pet/pet.model';

export enum Status {
  Waitlist = 0,
  Confirmed = 1,
  CheckedIn = 2,
  Pending = 9,
  Declined = 10,
}
export enum BookingType {
  INTERVIEW_REQUEST,
  RESERVATION,
}

export interface IParsedBooking
  // Remove the `__typename` to avoid type check between `Pet` and `PetWithStatus`.
  extends Omit<Booking, '__typename'> {
  // #region -> Custom Flags <-
  // ----------

  isInterviewRequest?: boolean;
  pets?: Array<ParsedPet>;
  franchise?: FranchiseLocation;

  // ----------
  // #endregion

  // ///////////////////////////
}

// #region -> Booking Class <-
// ----------

export class ParsedBooking implements IParsedBooking {
  // #region -> Property <-
  // ----------

  id?: string;
  title?: string;
  type?: string;
  categories?: Array<string>;
  startDate?: string;
  endDate?: string;
  status?: number;
  pets?: Array<ParsedPet>;
  franchise?: FranchiseLocation;
  items?: Array<Item>;
  declineReason?: string;

  isInterviewRequest?: boolean;

  statusTypeTextUI?: string;
  bookingTitleUI?: string;
  bookingDateUI?: string;
  statusMessageUI?: string;
  petNamesUI?: string;
  addressUI?: string;
  // ----------
  // #endregion

  /////////////////////////////
  // #region -> Constructor <-
  // ----------

  constructor(newBooking: IParsedBooking) {
    this.id = newBooking.id || undefined;
    this.title = newBooking.title || undefined;
    this.type = newBooking.type || undefined;
    this.categories = newBooking.categories || [];
    this.startDate = newBooking.startDate || undefined;
    this.endDate = newBooking.endDate || undefined;
    this.status = newBooking.status;
    this.pets = newBooking.pets || [];
    this.franchise = newBooking.franchise || undefined;
    this.items = newBooking.items || [];
    this.declineReason = newBooking.declineReason || undefined;
    this.isInterviewRequest = newBooking.isInterviewRequest || false;
    this.statusTypeTextUI = this.getStatusTypeText();
    this.bookingTitleUI = this.getBookingTitle();
    this.bookingDateUI = this.getBookingDate();
    this.statusMessageUI = this.getBookingStatusMessage();
    this.petNamesUI = this.getPetNames();
    this.addressUI = this.getAddress();
  }

  // ----------
  // #endregion
  /////////////////////////////

  // #region -> Method <-
  // ----------
  private getStatusTypeText(): string {
    switch (this.status) {
      case 0:
        return 'Waitlist';
      case 1:
        return 'Confirmed';
      case 2:
        return 'In Camp';
      case 9:
        return 'Pending';
      case 10:
        return 'Declined';
      default:
        return 'Unknown';
    }
  }

  private getBookingTitle(): string {
    return this.title
      ? this.title.charAt(0) === '.'
        ? // remove leading period
          this.title.substring(1)
        : this.title
      : this.categories.join(', ');
  }

  private getBookingDate(): string {
    const format = 'mediumDate';
    const locale = 'en-US';
    return this.startDate === this.endDate
      ? formatDate(this.startDate, format, locale)
      : formatDate(this.startDate, format, locale) +
          ' - ' +
          formatDate(this.endDate, format, locale);
  }

  private getBookingStatusMessage(): string {
    if (this.status === Status.Confirmed) {
      return this.getBookingDate();
    } else if (this.status === Status.Pending) {
      // return message if booking is a pending interview request
      if (this.categories.includes('Interview')) {
        return 'CBW staff will contact you shortly';
      } else {
        return this.getBookingDate();
      }
    }
    return this.getBookingDate();
  }

  private getPetNames(): string {
    const petNames = [];
    this.pets.forEach((pet) => {
      petNames.push(`${pet.firstName} ${pet.lastName}`);
    });
    return petNames.join(', ');
  }

  private getAddress() {
    const { address1, address2, city, state, zipcode } = this.franchise;
    return (
      address1 +
      ' ' +
      (address2 ? address2.trim() : '') +
      ', ' +
      city +
      ', ' +
      state +
      ' ' +
      zipcode
    );
  }
  // ----------
  // #endregion

  /////////////////////////////
}

// ----------
// #endregion

// ///////////////////////////
